// FRONT-END ANALYTICS (owner, 2026-10-08): "i want to know what each user is doing, when they are doing it, what they clicked
// on, when they scroll, what they type, how long it takes to get a response, what the response was". Every event is queued
// here and posted in batches to the API, which keeps it under the person's own id; nothing is sent to a third party. The
// password field is the one thing never read. The analyst that reads it back is the /self-improve skill, not the app.

export interface TelemetryEvent {
  at: string
  kind: string
  path: string
  target?: string
  detail?: unknown
  durationMs?: number
  status?: number
}

const SESSION_KEY = 'scout.telemetry-session'
const FLUSH_EVERY_MS = 5_000
const FLUSH_AT = 40
const MAX_TEXT = 2_000
const PREVIEW = 1_500

let queue: TelemetryEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let endpoint: (() => string | null) | null = null
let token: (() => string | null) | null = null
let installed = false
let dropped = 0

export function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) { id = crypto.randomUUID(); sessionStorage.setItem(SESSION_KEY, id) }
    return id
  } catch { return 'no-storage' }
}

function here(): string { return typeof location === 'undefined' ? '' : location.pathname + location.search }

/** Queue one event. Safe to call before install: the queue waits. */
export function record(kind: string, fields: Omit<TelemetryEvent, 'at' | 'kind' | 'path'> = {}): void {
  queue.push({ at: new Date().toISOString(), kind, path: here(), ...fields })
  if (queue.length >= FLUSH_AT) void flush()
  else if (!timer) timer = setTimeout(() => void flush(), FLUSH_EVERY_MS)
}

/** Post what is queued. Keepalive, so a batch sent as the tab closes still lands. */
export async function flush(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null }
  if (queue.length === 0 || !endpoint) return
  const url = endpoint()
  if (!url) return
  const batch = queue
  queue = []
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token?.()
  if (t) headers.Authorization = `Bearer ${t}`
  try {
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ sessionId: sessionId(), dropped, events: batch }), keepalive: true })
    if (!res.ok && res.status !== 401) dropped += batch.length
    else dropped = 0
  } catch { dropped += batch.length }
}

/** What the person clicked, as words: the control, its text, where it leads. Pure. */
export function describeTarget(el: Element | null): { target: string; text: string; href?: string } {
  if (!el) return { target: '', text: '' }
  const control = el.closest('button, a, [role="button"], input, select, textarea, label, summary, [data-telemetry]') ?? el
  const tag = control.tagName.toLowerCase()
  const id = control.id ? `#${control.id}` : ''
  const cls = typeof control.className === 'string' && control.className ? '.' + control.className.trim().split(/\s+/).slice(0, 3).join('.') : ''
  const named = control.getAttribute('data-telemetry') ?? control.getAttribute('aria-label') ?? control.getAttribute('title') ?? control.getAttribute('name') ?? ''
  const text = (control.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)
  const href = tag === 'a' ? control.getAttribute('href') ?? undefined : undefined
  const chain = [] as string[]
  let p: Element | null = control.parentElement
  while (p && chain.length < 3) {
    const seg = p.tagName.toLowerCase() + (p.id ? `#${p.id}` : typeof p.className === 'string' && p.className ? '.' + p.className.trim().split(/\s+/)[0] : '')
    chain.unshift(seg)
    p = p.parentElement
  }
  return { target: `${chain.join(' > ')}${chain.length ? ' > ' : ''}${tag}${id}${cls}${named ? `[${named}]` : ''}`, text, href }
}

function fieldOf(el: Element): { field: string; secret: boolean } {
  const input = el as HTMLInputElement
  const type = (input.type ?? '').toLowerCase()
  const auto = (input.getAttribute('autocomplete') ?? '').toLowerCase()
  const secret = type === 'password' || auto.includes('password')
  const field = input.getAttribute('data-telemetry') ?? input.getAttribute('aria-label') ?? input.getAttribute('name') ?? input.id ?? input.getAttribute('placeholder') ?? el.tagName.toLowerCase()
  return { field, secret }
}

/** Install the listeners once. `endpointUrl` answers the spoke's telemetry route (null while there is no spoke). */
export function install(endpointUrl: () => string | null, bearer: () => string | null): void {
  endpoint = endpointUrl
  token = bearer
  if (installed || typeof window === 'undefined') return
  installed = true
  record('open', { detail: { w: window.innerWidth, h: window.innerHeight, ua: navigator.userAgent, lang: navigator.language, referrer: document.referrer } })

  document.addEventListener('click', (e) => {
    const t = describeTarget(e.target as Element | null)
    record('click', { target: t.target, detail: { text: t.text, href: t.href, x: Math.round(e.clientX), y: Math.round(e.clientY) } })
  }, true)

  let scrollTimer: ReturnType<typeof setTimeout> | null = null
  window.addEventListener('scroll', () => {
    if (scrollTimer) clearTimeout(scrollTimer)
    scrollTimer = setTimeout(() => {
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      record('scroll', { detail: { y: Math.round(window.scrollY), max: Math.round(max), fraction: max > 0 ? Math.round((window.scrollY / max) * 100) / 100 : 1 } })
    }, 400)
  }, { passive: true })

  const typing = new Map<Element, ReturnType<typeof setTimeout>>()
  const typed = (el: Element, final: boolean) => {
    const { field, secret } = fieldOf(el)
    const value = secret ? '' : ((el as HTMLInputElement).value ?? (el as HTMLElement).textContent ?? '')
    record(final ? 'typed' : 'typing', { target: field, detail: { length: value.length, text: secret ? '[password]' : value.slice(0, MAX_TEXT) } })
  }
  document.addEventListener('input', (e) => {
    const el = e.target as Element | null
    if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el as HTMLElement).isContentEditable)) return
    const pending = typing.get(el)
    if (pending) clearTimeout(pending)
    typing.set(el, setTimeout(() => { typing.delete(el); typed(el, false) }, 1_200))
  }, true)
  document.addEventListener('change', (e) => {
    const el = e.target as Element | null
    if (!el) return
    const pending = typing.get(el)
    if (pending) { clearTimeout(pending); typing.delete(el) }
    if (el instanceof HTMLSelectElement) record('chose', { target: fieldOf(el).field, detail: { value: el.value, label: el.selectedOptions[0]?.text ?? '' } })
    else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) typed(el, true)
  }, true)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const el = e.target as Element | null
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        const pending = typing.get(el)
        if (pending) { clearTimeout(pending); typing.delete(el) }
        typed(el, true)
        record('enter', { target: fieldOf(el).field })
      }
    }
  }, true)

  window.addEventListener('error', (e) => record('error', { detail: { message: String(e.message).slice(0, 500), source: e.filename, line: e.lineno } }))
  window.addEventListener('unhandledrejection', (e) => record('error', { detail: { message: String((e.reason && (e.reason.message ?? e.reason)) ?? 'rejection').slice(0, 500) } }))
  document.addEventListener('visibilitychange', () => record(document.visibilityState === 'hidden' ? 'hidden' : 'visible'))
  let resizeTimer: ReturnType<typeof setTimeout> | null = null
  window.addEventListener('resize', () => { if (resizeTimer) clearTimeout(resizeTimer); resizeTimer = setTimeout(() => record('resize', { detail: { w: window.innerWidth, h: window.innerHeight } }), 500) })
  window.addEventListener('pagehide', () => { record('close'); void flush() })
}

/** A route change, from the router. */
export function recordRoute(from: string, to: string): void { record('route', { detail: { from, to } }) }

/** An API call as the person experienced it: how long, what came back. Called by the API client. */
export function recordApi(method: string, path: string, status: number, durationMs: number, requestBytes: number, response: string | null, error?: string): void {
  if (path.includes('/scout/telemetry')) return
  const preview = response === null ? undefined : response.slice(0, PREVIEW)
  record(error ? 'api-error' : 'api', { target: `${method} ${path}`, status, durationMs: Math.round(durationMs), detail: { requestBytes, responseBytes: response?.length ?? 0, response: path.includes('/api/auth/') ? undefined : preview, error } })
}

/** For tests: what is waiting. */
export function pending(): TelemetryEvent[] { return queue }
export function resetForTests(): void { queue = []; if (timer) { clearTimeout(timer); timer = null }; dropped = 0 }
